import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Lock } from "lucide-react";

export default function AdminProfile() {
  const { user, fetchMe } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      setName(user.name || "");
      setPhone(user.phone || "");
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { name, phone });
      await fetchMe();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return toast.error("New passwords don't match");
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    setSavingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      toast.success("Password changed successfully");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingPassword(false); }
  };

  if (!user || user === false) return null;

  return (
    <div className="space-y-8" data-testid="admin-profile-page">
      <h2 className="font-serif-display text-2xl">Your profile</h2>

      <form onSubmit={saveProfile} className="border border-[hsl(165_37%_17%/0.1)] rounded-md bg-white p-6 space-y-4 max-w-xl">
        <div className="flex items-center gap-2 mb-2">
          <User className="h-4 w-4 text-[hsl(10_51%_55%)]" />
          <h3 className="font-serif-display text-lg">Personal details</h3>
        </div>
        <div>
          <Label htmlFor="p-email">Email</Label>
          <Input id="p-email" value={user.email} disabled className="mt-2 bg-[hsl(42_22%_92%)]" data-testid="profile-email" />
          <p className="text-xs text-[hsl(100_3%_30%)] mt-1">Email cannot be changed from this screen.</p>
        </div>
        <div>
          <Label htmlFor="p-name">Full name</Label>
          <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-2" data-testid="profile-name" />
        </div>
        <div>
          <Label htmlFor="p-phone">Phone</Label>
          <Input id="p-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 ..." className="mt-2" data-testid="profile-phone" />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={savingProfile} className="btn-forest rounded-full px-6" data-testid="profile-save">
            {savingProfile ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>

      <form onSubmit={changePassword} className="border border-[hsl(165_37%_17%/0.1)] rounded-md bg-white p-6 space-y-4 max-w-xl">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="h-4 w-4 text-[hsl(10_51%_55%)]" />
          <h3 className="font-serif-display text-lg">Change password</h3>
        </div>
        <div>
          <Label htmlFor="p-current">Current password</Label>
          <Input id="p-current" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="mt-2" data-testid="profile-current-password" />
        </div>
        <div>
          <Label htmlFor="p-new">New password</Label>
          <Input id="p-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={6} required className="mt-2" data-testid="profile-new-password" />
          <p className="text-xs text-[hsl(100_3%_30%)] mt-1">At least 6 characters. Use letters + numbers + symbols for the strongest password.</p>
        </div>
        <div>
          <Label htmlFor="p-confirm">Confirm new password</Label>
          <Input id="p-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="mt-2" data-testid="profile-confirm-password" />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={savingPassword} className="btn-terracotta rounded-full px-6" data-testid="profile-change-password">
            {savingPassword ? "Updating…" : "Change password"}
          </Button>
        </div>
      </form>
    </div>
  );
}
